<script setup>
import { computed, ref } from 'vue'

import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import FormButton from '../Atoms/FormButton.vue';
import Badge from '../Badge.vue';
import IconBase from '../icon/IconBase.vue';
import IconChevronLeft from '../icon/IconChevronLeft.vue';
import MainSidebarItemIcon from './MainSidebarItemIcon.vue';

const props = defineProps({
  expanded: {
    type: Boolean,
  },
  title: {
    type: String,
  },
  icon: {
    type: Object,
  },
  to: {
    type: String,
  },
  width: {
    type: [Number, String],
    default: 16
  },
  height: {
    type: [Number, String],
    default: 16
  },
  widthViewBox: {
    type: [Number, String],
    default: 16
  },
  heightViewBox: {
    type: [Number, String],
    default: 16
  },
  badgeTitle: {
    type: String,
    default: null
  },
  withAnimation: {
    type: Boolean,
    default: false
  },
  onClick: Function,
})

const route = useRoute()

const isExternalLink = computed(() => {
  return typeof props.to === 'string' && props.to.startsWith('http')
})

// Settings groups several sibling routes under one sidebar item, so keep it
// highlighted across all of them (but not the separate Team / Billing items).
const SETTINGS_ROUTES = ['/manage/account', '/manage/developers', '/manage/api', '/manage/vmix-token', '/manage/captioning', '/manage/overage', '/manage/security']

const isActive = computed(() => {
  if ((props.to === '/scheduler') && route.path.startsWith('/scheduled')) return true
  if ((props.to === '/dashboard') && route.path.startsWith('/livestreams')) return true
  if ((props.to === '/dashboard') && route.path.startsWith('/multistreams')) return true
  if (props.to === '/manage/account') return SETTINGS_ROUTES.some((p) => route.path.startsWith(p))
  return route.path.startsWith(props.to)
})

</script>

<template>
  <a v-if="isExternalLink" target="_blank" :href="props.to" class="text-base md:text-tiny text-surface-8 hover:text-white hover:bg-surface-2 transition-colors duration-150 flex items-center gap-x-2.5 leading-none !py-1 h-8 hover:no-underline rounded-md" :class="props.expanded ? '!px-3' : '!px-2 w-8'">
    <MainSidebarItemIcon :expanded="props.expanded" :title="props.title" :icon="props.icon" :width="props.width" :height="props.height" :widthViewBox="props.widthViewBox" :heightViewBox="props.heightViewBox" :with-animation="props.withAnimation" />
    <span :class="props.expanded ? 'opacity-100' : 'opacity-0'" class="translate-all truncate leading-5">{{ props.title }}</span>
  </a>
  <router-link v-else-if="props.to" :to="{ path: props.to }" class="text-base md:text-tiny text-surface-8 hover:text-white hover:bg-surface-2 transition-colors duration-150 flex items-center gap-x-2.5 leading-none !py-1 h-8 hover:no-underline rounded-md relative" :class="[props.expanded ? '!px-3' : '!px-2 w-8', {'text-white bg-main-transparent [&_svg]:text-main-light hover:!bg-main-transparent': isActive}]">
    <span v-if="isActive" class="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-[3px] rounded-r bg-main"></span>
    <MainSidebarItemIcon :expanded="props.expanded" :title="props.title" :icon="props.icon" :width="props.width" :height="props.height" :widthViewBox="props.widthViewBox" :heightViewBox="props.heightViewBox" :with-animation="props.withAnimation" />
    <span :class="props.expanded ? 'opacity-100' : 'opacity-0'" class="translate-all truncate leading-5">{{ props.title }}</span>
    <Badge v-if="badgeTitle" type="main" class="badge_beta ml-2">{{ badgeTitle }}</Badge>
  </router-link>
  <FormButton v-else @click="onClick" :customClasses="`text-base md:text-tiny text-surface-8 hover:text-white hover:bg-surface-2 transition-colors duration-150 flex items-center gap-x-2.5 leading-none !py-1 h-8 hover:no-underline rounded-md ${props.expanded ? '!px-3' : '!px-2 w-8'}`">
    <MainSidebarItemIcon :expanded="props.expanded" :title="props.title" :icon="props.icon" :width="props.width" :height="props.height" :widthViewBox="props.widthViewBox" :heightViewBox="props.heightViewBox" :with-animation="props.withAnimation" />
    <span :class="props.expanded ? 'opacity-100' : 'opacity-0'" class="translate-all truncate leading-5">{{ props.title }}</span>
  </FormButton>
</template>